// Limon Ice offer pop-up (sections/limon-offer-popup.liquid, B22 #64). No library, no tracking.
// - Opens after data-delay ms OR once data-scroll (0-1) of the page is scrolled, never before data-min-delay ms.
// - Waits (and retries) while the cart drawer, the menu drawer, the search modal, a quick-add modal or
//   Shopify's cookie banner is open, or while a primary button sits where the pop-up would appear.
// - Every close (x, Escape, backdrop, one of its links) stores Date.now() in localStorage under STORAGE_KEY;
//   it stays closed for data-days days. Storage access is wrapped in try/catch (private mode, blocked storage).
// - Phones (< 750 px): bottom sheet, aria-modal="true", focus trap, above the sticky add-to-cart bar.
//   From 750 px: non-modal card, aria-modal="false". Focus moves to the close button on open and returns
//   to the previously focused element on close.
(() => {
  const STORAGE_KEY = 'limon-offer-dismissed-at';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const RETRY_MS = 1000;
  const PHONE_QUERY = '(max-width: 749px)';
  const AVOID_SELECTOR = [
    '.limon-hero__button',
    '.banner__buttons .button',
    '.product-form__submit',
    '.shopify-payment-button__button',
    '#checkout',
    '.cart__checkout-button',
    'button[name="checkout"]',
  ].join(',');

  const readDismissed = () => {
    try {
      return Number(window.localStorage.getItem(STORAGE_KEY)) || 0;
    } catch (error) {
      return 0;
    }
  };

  const writeDismissed = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (error) {
      /* storage blocked: the pop-up may show again on the next page, nothing else breaks */
    }
  };

  const isShown = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    const style = getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none';
  };

  const overlayOpen = () => {
    const body = document.body;
    if (body && /(^|\s)overflow-hidden/.test(body.className)) return true;
    if (document.querySelector('cart-drawer.active, cart-drawer.is-open')) return true;
    if (document.querySelector('header-drawer details[open], .menu-drawer-container[open]')) return true;
    if (document.querySelector('details-modal details[open], .search-modal[open], predictive-search[open]')) return true;
    if (document.querySelector('quick-add-modal[open], modal-dialog[open]')) return true;
    const banner = document.getElementById('shopify-pc__banner');
    if (banner && isShown(banner)) return true;
    return false;
  };

  function stickyBarOffset() {
    if (!window.matchMedia(PHONE_QUERY).matches) return 0;
    const bar = document.querySelector('limon-sticky-atc');
    if (!bar || !bar.classList.contains('is-visible') || bar.classList.contains('is-unavailable')) return 0;
    const style = getComputedStyle(bar);
    if (style.display === 'none') return 0;
    return bar.offsetHeight || 0;
  }

  class LimonOffer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.limon-offer__panel');
      this.closeButton = root.querySelector('.limon-offer__close');
      this.days = Number(root.dataset.days) || 7;
      this.delay = Number(root.dataset.delay) || 8000;
      this.minDelay = Number(root.dataset.minDelay) || 3000;
      this.scrollShare = Number(root.dataset.scroll) || 0.4;
      this.start = performance.now();
      this.wanted = false;
      this.isOpen = false;
      this.previousFocus = null;
      this.phone = window.matchMedia(PHONE_QUERY);

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.updateOffset = this.updateOffset.bind(this);
      this.updateMode = this.updateMode.bind(this);

      root.querySelectorAll('[data-limon-offer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      root.querySelectorAll('[data-limon-offer-cta]').forEach((el) =>
        el.addEventListener('click', () => this.close({ restoreFocus: false }))
      );

      if (root.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(root)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(root)) this.close({ store: false });
        });
        return;
      }

      const dismissedAt = readDismissed();
      if (dismissedAt && Date.now() - dismissedAt < this.days * DAY_MS) return;

      this.timer = window.setTimeout(() => this.request(), this.delay);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    onScroll() {
      if (this.wanted || this.scrollQueued) return;
      this.scrollQueued = true;
      window.requestAnimationFrame(() => {
        this.scrollQueued = false;
        const doc = document.documentElement;
        const max = Math.max(1, doc.scrollHeight - window.innerHeight);
        if (window.scrollY / max >= this.scrollShare) this.request();
      });
    }

    request() {
      if (this.wanted) return;
      this.wanted = true;
      window.clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      this.tryOpen();
    }

    tryOpen() {
      if (this.isOpen) return;
      const wait = this.minDelay - (performance.now() - this.start);
      if (wait > 0) {
        this.retry = window.setTimeout(() => this.tryOpen(), wait);
        return;
      }
      if (document.hidden || overlayOpen() || this.coversPrimaryButton()) {
        this.retry = window.setTimeout(() => this.tryOpen(), RETRY_MS);
        return;
      }
      this.open();
    }

    // Measures the panel where it would appear and checks it against the primary buttons on screen.
    coversPrimaryButton() {
      this.updateOffset();
      this.root.classList.add('is-measuring');
      this.root.hidden = false;
      const zone = this.panel.getBoundingClientRect();
      this.root.hidden = true;
      this.root.classList.remove('is-measuring');
      const top = zone.top - 8;
      const bottom = zone.bottom + 8;
      return Array.from(document.querySelectorAll(AVOID_SELECTOR)).some((el) => {
        if (this.root.contains(el) || !isShown(el)) return false;
        const r = el.getBoundingClientRect();
        return r.bottom > top && r.top < bottom && r.right > zone.left && r.left < zone.right;
      });
    }

    updateOffset() {
      const offset = stickyBarOffset();
      this.root.style.setProperty('--limon-offer-bottom', `${offset}px`);
      this.root.classList.toggle('limon-offer--above-bar', offset > 0);
    }

    updateMode() {
      this.panel.setAttribute('aria-modal', this.phone.matches ? 'true' : 'false');
      this.updateOffset();
    }

    watchStickyBar() {
      const bar = document.querySelector('limon-sticky-atc');
      if (!bar || !('MutationObserver' in window)) return;
      this.barObserver = new MutationObserver(this.updateOffset);
      this.barObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.previousFocus = document.activeElement;
      this.updateMode();
      this.root.hidden = false;
      // Force a style flush so the slide-in transition runs from the closed state.
      void this.panel.offsetHeight;
      this.root.classList.add('is-open');
      this.closeButton.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.updateOffset);
      this.phone.addEventListener?.('change', this.updateMode);
      this.watchStickyBar();
    }

    close({ store = true, restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (store) writeDismissed();
      this.root.classList.remove('is-open');
      this.root.hidden = true;
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.updateOffset);
      this.phone.removeEventListener?.('change', this.updateMode);
      if (this.barObserver) this.barObserver.disconnect();
      const previous = this.previousFocus;
      this.previousFocus = null;
      if (restoreFocus && previous && previous !== document.body && document.contains(previous) && previous.focus) {
        previous.focus({ preventScroll: true });
      } else if (this.root.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.phone.matches) return;
      // Simple focus trap for the modal bottom sheet.
      const focusables = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled])')).filter(isShown);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !this.panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const init = () => document.querySelectorAll('[data-limon-offer]').forEach((root) => new LimonOffer(root));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
